import { Button } from "@/components";
import NumberField from "@/components/form/components/NumberField";
import InputField from "@/components/InputField";
import { Form } from "@/components/ui/form";
import { getGoogleAccessToken } from "@/lib/google";
import { notifyError, notifySuccess } from "@/lib/notification";
import { cn } from "@/lib/utils";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const loginSchema = z.object({
  identifier: z
    .string()
    .min(1, "Please enter an email or phone number.")
    .refine((val) => {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      const isPhone = /^\d{10}$/.test(val);
      return isEmail || isPhone;
    }, "Please enter a valid email address or 10-digit phone number."),
});

const verifySchema = z.object({
  otp: z.string().min(4, "OTP must be at least 4 digits."),
});

type LoginFormType = z.infer<typeof loginSchema>;
type VerifyFormType = z.infer<typeof verifySchema>;

const Login = () => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const [step, setStep] = useState<"SEND_OTP" | "VERIFY_OTP">("SEND_OTP");
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState<"email" | "phone">(
    "email"
  );

  const form = useForm<LoginFormType>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: "",
    },
  });

  const verifyForm = useForm<VerifyFormType>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      otp: "",
    },
  });

  const otpMutation = useMutation({
    mutationFn: async ({
      type,
      identifier,
    }: {
      type: "email" | "phone";
      identifier: string;
    }) => {
      return await authService.sendOtp(type, identifier);
    },
    onSuccess: (_, variables) => {
      setIdentifier(variables.identifier);
      setIdentifierType(variables.type);
      setStep("VERIFY_OTP");
      notifySuccess(`OTP sent to your ${variables.type}!`);
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
          error?.message ||
          "Failed to send OTP. Please try again."
      );
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ otp }: { otp: string }) => {
      return await authService.verifyOtp(identifierType, identifier, otp);
    },
    onSuccess: (data) => {
      // Check for either access_token (OTP) or token (Google possibly)
      const token = data.access_token || data.token;
      if (token && data.data) {
        login(data.data, token);
        notifySuccess("Login successful!");
        navigate("/dashboard");
      } else {
        notifyError("Token missing from response");
      }
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
          error?.message ||
          "Invalid OTP. Please try again."
      );
    },
  });

  // TanStack Query mutation for Google
  const mutation = useMutation({
    mutationFn: async (googleToken: string) => {
      return await authService.loginWithGoogle(googleToken);
    },
    onSuccess: (data) => {
      const token = data.access_token || data.token;
      if (token && data.data) {
        login(data.data, token);
        notifySuccess("Login successful!");
        navigate("/dashboard");
      } else {
        notifyError("Token missing from response");
      }
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
          error?.message ||
          "Login failed. Please try again."
      );
    },
  });

  const onSubmit = (data: LoginFormType) => {
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.identifier);
    const type = isEmail ? "email" : "phone";
    otpMutation.mutate({ type, identifier: data.identifier });
  };

  const onVerifySubmit = (data: VerifyFormType) => {
    verifyOtpMutation.mutate({ otp: data.otp });
  };

  const handleLogin = async () => {
    try {
      const googleToken = await getGoogleAccessToken();
      mutation.mutate(googleToken);
    } catch (err: any) {
      notifyError(err?.message || "Google login failed.");
    }
  };

  if (import.meta.env.VITE_MODE === "development") {
    return (
      <div className="flex h-screen items-center justify-center gap-4">
        🔐 Login Page (Public)
        <Button onClick={() => navigate("/dev-login")}>Dev Login</Button>
      </div>
    );
  }

  return (
    <div className="m-auto flex min-h-screen w-screen items-center justify-center p-6">
      <div className="mx-auto flex w-full max-w-md flex-col rounded-xl border border-neutral-300 bg-white p-8 shadow">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold">Welcome College</h1>
          <p className="mt-2 text-sm text-neutral-500">
            {step === "SEND_OTP"
              ? "Sign in to your account"
              : `Enter the OTP sent to ${identifier}`}
          </p>
        </div>

        {step === "SEND_OTP" && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <InputField
                autoFocus
                type="text"
                name="identifier"
                placeholder="Email or Phone Number"
                disabled={otpMutation.isPending || mutation.isPending}
              />

              <Button
                type="submit"
                className={cn(
                  "w-full transition-colors",
                  otpMutation.isPending || mutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                disabled={otpMutation.isPending || mutation.isPending}
              >
                {otpMutation.isPending ? "Sending OTP..." : "Continue with OTP"}
              </Button>
            </form>
          </Form>
        )}
        {step === "VERIFY_OTP" && (
          <Form {...verifyForm}>
            <form
              onSubmit={verifyForm.handleSubmit(onVerifySubmit)}
              className="space-y-4"
            >
              <NumberField
                autoFocus
                name="otp"
                placeholder="Enter OTP"
                disabled={verifyOtpMutation.isPending}
              />

              <Button
                type="submit"
                className={cn(
                  "w-full transition-colors",
                  verifyOtpMutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                disabled={verifyOtpMutation.isPending}
              >
                {verifyOtpMutation.isPending ? "Verifying..." : "Verify OTP"}
              </Button>
              <div className="mt-2 text-center">
                <Button
                  type="button"
                  className={cn(
                    "w-full transition-colors",
                    verifyOtpMutation.isPending
                      ? "opacity-50 hover:cursor-not-allowed"
                      : "hover:cursor-pointer"
                  )}
                  onClick={() => setStep("SEND_OTP")}
                  disabled={verifyOtpMutation.isPending}
                  variant={"outline"}
                >
                  Back to login
                </Button>
              </div>
            </form>
          </Form>
        )}
        {step === "SEND_OTP" && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-neutral-500">OR</span>
              </div>
            </div>

            <Button
              className={cn(
                "flex w-full items-center justify-center gap-2 border border-neutral-300 bg-white text-black transition-colors hover:bg-neutral-50",
                mutation.isPending
                  ? "hover:cursor-not-allowed"
                  : "hover:cursor-pointer"
              )}
              onClick={handleLogin}
              disabled={otpMutation.isPending || mutation.isPending}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {mutation.isPending ? "Logging in..." : "Continue with Google"}
            </Button>

            <div className="mt-6 text-center text-sm text-neutral-600">
              New to the platform?{" "}
              <Button
                type="button"
                className="hover:cursor-pointer"
                onClick={() => navigate("/register-college")}
                variant={"link"}
              >
                Register your college
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
